import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon,
  IconButton, MenuItem, Paper, Snackbar, Step, StepLabel, Stepper, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { EmpresaService } from '../../../shared/services/api/cadastros/Empresa';
import { ParceirosService } from '../../../shared/services/api/cadastros/ParceirosService';
import { MunicipiosService } from '../../../shared/services/api/cadastros/MunicipiosService';
import { IListagemTransportadoras, TransportadoraService } from '../../../shared/services/api/cadastros/TransportadoraService';
import { IDetalheVeiculo, IListagemVeiculos, VeiculosService } from '../../../shared/services/api/cadastros/VeiculosService';
import { INfeParaMdfe, INovoMdfe, MdfeService, daListaPg, paraListaPg, situacaoMdfe } from '../../../shared/services/api/Fiscal/MdfeService';
import { DialogBuscarNfe } from './DialogBuscarNfe';

// Emissão de MDF-e (Fiscal > MDF-e > Adicionar) — layout em cartões (dados
// gerais, viagem, cidades de carregamento/descarregamento, rota, veículo,
// motoristas, seguro, totais) com barra de ações no rodapé. Só os campos que
// o backend de MDF-e tem: o corpo vai no formato do POST /mdfe —
// [cabeçalho, documentos[], seguros[], veículos[]] (ver MdfeService.create).
// /mdfe/novo cria (POST); /mdfe/:id abre pelo GET /mdfe/:id e salva com PUT.

const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];
// Código IBGE da UF (cUF do MDF-e).
const CODIGO_UF: Record<string, number> = {
  RO: 11, AC: 12, AM: 13, RR: 14, PA: 15, AP: 16, TO: 17, MA: 21, PI: 22, CE: 23, RN: 24, PB: 25, PE: 26, AL: 27, SE: 28,
  BA: 29, MG: 31, ES: 32, RJ: 33, SP: 35, PR: 41, SC: 42, RS: 43, MS: 50, MT: 51, GO: 52, DF: 53,
};
const TIPOS_TRANSPORTADOR = [
  { valor: 1, rotulo: '1 - Empresa de Transporte de Cargas (ETC)' },
  { valor: 2, rotulo: '2 - Transportador Autônomo (TAC)' },
  { valor: 3, rotulo: '3 - Cooperativa de Transporte (CTC)' },
];
const TIPOS_RODADO = [
  { valor: '01', rotulo: '01 - Truck' }, { valor: '02', rotulo: '02 - Toco' }, { valor: '03', rotulo: '03 - Cavalo Mecânico' },
  { valor: '04', rotulo: '04 - VAN' }, { valor: '05', rotulo: '05 - Utilitário' }, { valor: '06', rotulo: '06 - Outros' },
];
const TIPOS_CARROCERIA = [
  { valor: '00', rotulo: '00 - Não aplicável' }, { valor: '01', rotulo: '01 - Aberta' }, { valor: '02', rotulo: '02 - Fechada/Baú' },
  { valor: '03', rotulo: '03 - Granelera' }, { valor: '04', rotulo: '04 - Porta Container' }, { valor: '05', rotulo: '05 - Sider' },
];
const UNIDADES_CARGA = [{ valor: '01', rotulo: '01 - KG' }, { valor: '02', rotulo: '02 - TON' }];
const UF_DO_CODIGO: Record<string, string> = Object.fromEntries(Object.entries(CODIGO_UF).map(([uf, c]) => [String(c), uf]));
const ROTULO_SITUACAO = {
  pendente: { rotulo: 'Pendente', cor: 'warning' }, autorizado: { rotulo: 'Autorizado', cor: 'success' },
  cancelado: { rotulo: 'Cancelado', cor: 'error' }, encerrado: { rotulo: 'Encerrado', cor: 'default' },
} as const;
const ETAPAS = ['Dados Gerais', 'Rota', 'Veículos/Condutores', 'Finalização'];

interface IMunicipio { codigo: string; nome: string; uf: string }
interface IDescarga { id: string; municipio: IMunicipio | null; chavesNfe: string; chavesCte: string }
interface ICondutor { id: string; xnome: string; cpf: string }
interface ISeguroForm { id: string; responsavel: '1' | '2'; cnpjResponsavel: string; seguradora: string; cnpjSeguradora: string; apolice: string; averbacoes: string }

const somenteDigitos = (valor: string) => valor.replace(/\D/g, '');
// Chaves de acesso (44 dígitos) digitadas uma por linha (ou separadas por espaço/vírgula).
const extrairChaves = (texto: string) => texto.split(/[\s,;]+/).map(somenteDigitos).filter(Boolean);
// Chave em grupos de 4 dígitos; número e série da nota saem da própria chave
// (posições 23-25 = série, 26-34 = nNF).
const formatarChave = (chave: string) => chave.replace(/(\d{4})(?=\d)/g, '$1 ');
const numeroDaChave = (chave: string) => String(Number(chave.slice(25, 34)));
const serieDaChave = (chave: string) => String(Number(chave.slice(22, 25)));
const formatarCpf = (cpf: string) => somenteDigitos(cpf).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

// Cartão de seção (ícone + título + ação à direita). Fora do componente da
// tela de propósito: definido dentro dele, seria recriado a cada render e os
// campos perderiam o foco a cada tecla.
const Cartao: React.FC<{ icone: string; titulo: string; acao?: React.ReactNode; children: React.ReactNode }> = ({ icone, titulo, acao, children }) => {
  const theme = useTheme();
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, height: '100%', boxShadow: theme.shadows[1] }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} gap={1} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon sx={{ fontSize: 18 }}>{icone}</Icon>
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>{titulo}</Typography>
        </Box>
        {acao}
      </Box>
      {children}
    </Paper>
  );
};

const BotaoAdicionar: React.FC<{ onClick: () => void; disabled?: boolean; children?: React.ReactNode }> = ({ onClick, disabled, children }) => (
  <Button size="small" variant="contained" color="success" disableElevation startIcon={<Icon>add</Icon>} onClick={onClick} disabled={disabled}>
    {children || 'Adicionar'}
  </Button>
);

const LinhaVazia: React.FC<{ colunas: number; texto: string }> = ({ colunas, texto }) => (
  <TableRow><TableCell colSpan={colunas} align="center"><Typography variant="body2" color="text.secondary" py={1}>{texto}</Typography></TableCell></TableRow>
);

// Busca de município — /municipioslist.
const CampoMunicipio: React.FC<{ valor: IMunicipio | null; aoMudar: (m: IMunicipio | null) => void; rotulo: string }> = ({ valor, aoMudar, rotulo }) => {
  const [opcoes, setOpcoes] = useState<IMunicipio[]>([]);
  const [busca, setBusca] = useState('');
  const { debounce } = useDebounce();
  useEffect(() => {
    debounce(() => {
      MunicipiosService.getAlllist(busca).then((r) => {
        if (!(r instanceof Error)) setOpcoes(r.data.map((m) => ({ codigo: String(m.idmunicipio), nome: m.cidade, uf: m.uf })));
      });
    });
  }, [busca]);
  return (
    <Autocomplete
      size="small" fullWidth options={opcoes} value={valor} filterOptions={(x) => x}
      getOptionLabel={(m) => `${m.nome} (${m.uf})`}
      isOptionEqualToValue={(a, b) => a.codigo === b.codigo}
      onInputChange={(_, texto, motivo) => { if (motivo === 'input') setBusca(texto); }}
      onChange={(_, novo) => aoMudar(novo)}
      noOptionsText="Digite o nome do município"
      renderInput={(params) => <TextField {...params} label={rotulo} />}
    />
  );
};

export const DetalheDeMdfe: React.FC = () => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { id = 'novo' } = useParams<'id'>();
  const editando = id !== 'novo';
  // Dados do MDF-e aberto que a tela não edita, mas o PUT precisa manter.
  const [carregando, setCarregando] = useState(editando);
  const [numeroSalvo, setNumeroSalvo] = useState<number | null>(null);
  const [statusSalvo, setStatusSalvo] = useState<number | null>(null);
  const [cmdfSalvo, setCmdfSalvo] = useState<number | null>(null);
  const [idveiculoSalvo, setIdveiculoSalvo] = useState<string | null>(null);
  const { debounce } = useDebounce();
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'error' | 'success'; texto: string } | null>(null);

  // Dados gerais / viagem
  const [emitente, setEmitente] = useState('');
  const [tpamb, setTpamb] = useState(2);
  const [serie, setSerie] = useState('1');
  const [dhemi, setDhemi] = useState(dayjs().format('YYYY-MM-DD'));
  const [dhiniviagem, setDhiniviagem] = useState(dayjs().format('YYYY-MM-DD'));
  const [tptrasnp, setTptrasnp] = useState(1);
  const [tpemis, setTpemis] = useState(1);
  const [ufni, setUfni] = useState('');
  const [uffim, setUffim] = useState('');
  const [contratante, setContratante] = useState<{ idparceiro: string; nome: string } | null>(null);
  const [opcoesContratante, setOpcoesContratante] = useState<{ idparceiro: string; nome: string }[]>([]);
  const [buscaContratante, setBuscaContratante] = useState('');
  // Carregamento / descarregamento / rota
  const [municipiosCarga, setMunicipiosCarga] = useState<IMunicipio[]>([]);
  const [municipioCargaNovo, setMunicipioCargaNovo] = useState<IMunicipio | null>(null);
  const [descargas, setDescargas] = useState<IDescarga[]>([]);
  const [municipioDescargaNovo, setMunicipioDescargaNovo] = useState<IMunicipio | null>(null);
  const [descargaEmEdicao, setDescargaEmEdicao] = useState<IDescarga | null>(null);
  const [ufsPercurso, setUfsPercurso] = useState<string[]>([]);
  // Veículo / motoristas
  const [placa, setPlaca] = useState('');
  const [renavam, setRenavam] = useState('');
  const [tara, setTara] = useState('');
  const [capkg, setCapkg] = useState('');
  const [capm3, setCapm3] = useState('');
  const [tprod, setTprod] = useState('01');
  const [tcar, setTcar] = useState('02');
  const [ufVeiculo, setUfVeiculo] = useState('');
  // Puxar o veículo do cadastro: transportadora → veículos dela → dados do veículo.
  const { debounce: debounceTransportadora } = useDebounce();
  const [transportadora, setTransportadora] = useState<IListagemTransportadoras | null>(null);
  const [opcoesTransportadora, setOpcoesTransportadora] = useState<IListagemTransportadoras[]>([]);
  const [buscaTransportadora, setBuscaTransportadora] = useState('');
  const [veiculosDaTransportadora, setVeiculosDaTransportadora] = useState<IListagemVeiculos[]>([]);
  const [veiculoCadastrado, setVeiculoCadastrado] = useState<IListagemVeiculos | null>(null);
  const [carregandoVeiculo, setCarregandoVeiculo] = useState(false);
  const [condutores, setCondutores] = useState<ICondutor[]>([]);
  const [condutorNovo, setCondutorNovo] = useState({ xnome: '', cpf: '' });
  // Seguro / totais
  const [seguros, setSeguros] = useState<ISeguroForm[]>([]);
  const [vcarga, setVcarga] = useState('');
  const [cunid, setCunid] = useState('01');
  const [qcarga, setQcarga] = useState('');
  const [openBuscarNfe, setOpenBuscarNfe] = useState(false);
  // Notas vinculadas: valor/peso das que vieram do sistema (pra descontar se
  // a nota for removida) e o campo de chave digitada.
  const [infoNotas, setInfoNotas] = useState<Record<string, { valor: number; pesoBruto: number }>>({});
  const [chaveManual, setChaveManual] = useState('');
  const [descargaManualId, setDescargaManualId] = useState('');

  // Ambiente, UF e nome do emitente vêm da empresa (a UF dá pra trocar).
  useEffect(() => {
    EmpresaService.getById().then((empresa) => {
      if (empresa instanceof Error) return;
      setTpamb(Number(empresa.tpamb) || 2);
      setEmitente(empresa.fantasia || empresa.nome || '');
      if (!editando && empresa.uf) { setUfni(empresa.uf); setUfVeiculo(empresa.uf); }
    });
  }, []);

  // Abrir um MDF-e salvo (GET /mdfe/:id) e preencher a tela.
  useEffect(() => {
    if (!editando) return;
    setCarregando(true);
    MdfeService.getById(id).then(async (r) => {
      setCarregando(false);
      if (r instanceof Error) { setMensagem({ tipo: 'error', texto: r.message }); return; }
      console.log('[MDF-e] GET /mdfe/' + id, r);
      const data = (valor: unknown) => (valor ? dayjs(String(valor)).format('YYYY-MM-DD') : '');
      setNumeroSalvo(r.nmdf ?? null);
      setStatusSalvo(r.status ?? null);
      setCmdfSalvo(r.cmdf ? Number(r.cmdf) : null);
      setTpamb(Number(r.tpamb) || 2);
      setSerie(String(r.serie ?? ''));
      setDhemi(data(r.dhemi));
      setDhiniviagem(data(r.dhiniviagem));
      setTptrasnp(Number(r.tptrasnp) || 1);
      setTpemis(Number(r.tpemis) || 1);
      setUfni(r.ufni || '');
      setUffim(r.uffim || '');
      setVcarga(r.vcarga !== null && r.vcarga !== undefined ? String(r.vcarga) : '');
      setQcarga(r.qcarga !== null && r.qcarga !== undefined ? String(r.qcarga) : '');
      if (r.cunid) setCunid(String(r.cunid).padStart(2, '0'));
      setMunicipiosCarga(daListaPg<{ cMunCarrega: string; xMunCarrega: string }>(r.infmuncarrega)
        .filter((m) => m && m.cMunCarrega)
        .map((m) => ({ codigo: String(m.cMunCarrega), nome: m.xMunCarrega, uf: UF_DO_CODIGO[String(m.cMunCarrega).slice(0, 2)] || '' })));
      setUfsPercurso(daListaPg<{ UFPer: string }>(r.infpercurso).map((p) => p?.UFPer).filter(Boolean));
      setDescargas((r.mdfedocs || []).map((d) => ({
        id: d.iddocs,
        municipio: { codigo: String(d.cmundescarga), nome: d.xmundescarga, uf: UF_DO_CODIGO[String(d.cmundescarga).slice(0, 2)] || '' },
        chavesNfe: daListaPg<{ chnfe: string }>(d.inffe).map((n) => n?.chnfe).filter(Boolean).join('\n'),
        chavesCte: daListaPg<{ chcte: string }>(d.infcte).map((c) => c?.chcte).filter(Boolean).join('\n'),
      })));
      setSeguros((r.mdfeseguro || []).map((s) => {
        const resp = daListaPg<{ respSeg?: string; cnpj?: string }>(s.infresp)[0] || {};
        const seg = daListaPg<{ xSeg?: string; cnpj?: string; nApol?: string }>(s.infseg)[0] || {};
        return {
          id: s.idseguro, responsavel: resp.respSeg === '2' ? '2' : '1', cnpjResponsavel: resp.cnpj || '',
          seguradora: seg.xSeg || '', cnpjSeguradora: seg.cnpj || '', apolice: seg.nApol || '',
          averbacoes: daListaPg<string>(s.naver).map(String).join('\n'),
        };
      }));
      const veiculo = (r.mdfveiculos || []).find((v) => v.idveiculos === r.idveiculotracado) || (r.mdfveiculos || [])[0];
      if (veiculo) {
        setIdveiculoSalvo(veiculo.idveiculos);
        setPlaca(veiculo.placa || '');
        setRenavam(veiculo.renavam || '');
        setTara(String(veiculo.tara ?? ''));
        setCapkg(String(veiculo.capkg ?? ''));
        setCapm3(String(veiculo.capm3 ?? ''));
        if (veiculo.tprod) setTprod(String(veiculo.tprod).padStart(2, '0'));
        if (veiculo.tcar) setTcar(String(veiculo.tcar).padStart(2, '0'));
        if (veiculo.uf) setUfVeiculo(veiculo.uf);
        setCondutores(daListaPg<{ xnome: string; cpf: string }>(veiculo.condutor)
          .filter((c) => c && c.cpf).map((c) => ({ id: uuidv4(), xnome: c.xnome, cpf: somenteDigitos(String(c.cpf)) })));
      }
      if (r.idparceiro) {
        const parceiro = await ParceirosService.getAllList('', r.idparceiro);
        const achado = parceiro instanceof Error ? undefined : parceiro.data.find((p) => p.idparceiro === r.idparceiro) || parceiro.data[0];
        setContratante({ idparceiro: r.idparceiro, nome: achado?.nome || 'Contratante' });
      }
    });
  }, [id]);
  const situacao = ROTULO_SITUACAO[situacaoMdfe(statusSalvo)];
  // Autorizado/cancelado/encerrado não se altera mais.
  const bloqueado = editando && situacaoMdfe(statusSalvo) !== 'pendente';

  // Transportadoras cadastradas (/transportadoraslist, busca por nome).
  useEffect(() => {
    debounceTransportadora(() => {
      TransportadoraService.getAllList(buscaTransportadora).then((r) => {
        if (!(r instanceof Error)) setOpcoesTransportadora(r.data);
      });
    });
  }, [buscaTransportadora]);

  // Veículos da transportadora escolhida (/veiculoslist?idtransportadora=).
  useEffect(() => {
    setVeiculoCadastrado(null);
    setVeiculosDaTransportadora([]);
    if (!transportadora) return;
    VeiculosService.getAllList(transportadora.idtransportadora).then((r) => {
      if (r instanceof Error) { setMensagem({ tipo: 'error', texto: r.message }); return; }
      setVeiculosDaTransportadora(r.data);
      if (!r.data.length) setMensagem({ tipo: 'error', texto: 'Essa transportadora não tem veículos cadastrados.' });
    });
  }, [transportadora]);

  // Escolheu o veículo: busca o cadastro completo (GET /veiculos/:id) e
  // preenche o cartão. Se a consulta falhar (ex.: sem permissão na tela de
  // veículos), usa os dados que já vieram na listagem.
  const preencherVeiculo = async (veiculo: IListagemVeiculos | null) => {
    setVeiculoCadastrado(veiculo);
    if (!veiculo) return;
    setCarregandoVeiculo(true);
    const detalhe = await VeiculosService.getById(veiculo.idveiculo);
    setCarregandoVeiculo(false);
    const dados: IDetalheVeiculo = detalhe instanceof Error ? veiculo : detalhe;
    const texto = (valor: unknown) => (valor === null || valor === undefined ? '' : String(valor));
    const codigo = (valor: unknown) => (texto(valor) === '' ? '' : texto(valor).padStart(2, '0'));
    setPlaca(texto(dados.placa).toUpperCase());
    setRenavam(somenteDigitos(texto(dados.renavam)));
    setTara(somenteDigitos(texto(Math.round(Number(dados.tara) || 0) || '')));
    setCapkg(somenteDigitos(texto(Math.round(Number(dados.capacidadekg) || 0) || '')));
    setCapm3(somenteDigitos(texto(Math.round(Number(dados.capacidadem3) || 0) || '')));
    if (dados.uf) setUfVeiculo(dados.uf);
    if (TIPOS_RODADO.some((o) => o.valor === codigo(dados.tiporoda))) setTprod(codigo(dados.tiporoda));
    if (TIPOS_CARROCERIA.some((o) => o.valor === codigo(dados.tipocarroceria))) setTcar(codigo(dados.tipocarroceria));
    setMensagem({ tipo: 'success', texto: `Dados do veículo ${texto(dados.placa).toUpperCase()} preenchidos.` });
  };

  useEffect(() => {
    debounce(() => {
      ParceirosService.getAllList(buscaContratante).then((r) => {
        if (!(r instanceof Error)) setOpcoesContratante(r.data.map((p) => ({ idparceiro: p.idparceiro, nome: p.nome })));
      });
    });
  }, [buscaContratante]);

  const totais = useMemo(() => ({
    qnfe: descargas.reduce((soma, d) => soma + extrairChaves(d.chavesNfe).length, 0),
    qcte: descargas.reduce((soma, d) => soma + extrairChaves(d.chavesCte).length, 0),
  }), [descargas]);

  // --- Validação por etapa (alimenta o stepper, o selo e o salvar) ----------
  const pendencias = useMemo(() => {
    const geral: string[] = [];
    if (serie === '' || !(Number(serie) >= 0)) geral.push('Informe a série.');
    if (!contratante) geral.push('Selecione o contratante.');
    if (!ufni || !uffim) geral.push('Informe a UF de carregamento e de descarregamento.');
    const rota: string[] = [];
    if (!descargas.length) rota.push('Adicione ao menos uma cidade de descarregamento.');
    for (const d of descargas) {
      const chaves = [...extrairChaves(d.chavesNfe), ...extrairChaves(d.chavesCte)];
      if (!chaves.length) rota.push(`Informe ao menos uma chave de NF-e ou CT-e para ${d.municipio?.nome}.`);
      const invalida = chaves.find((c) => c.length !== 44);
      if (invalida) rota.push(`Chave de acesso inválida (${invalida.length} dígitos, precisa ter 44): ${invalida}`);
    }
    const veiculo: string[] = [];
    if (!placa.trim()) veiculo.push('Informe a placa do veículo de tração.');
    if (!tara.trim()) veiculo.push('Informe a tara do veículo.');
    if (!condutores.length) veiculo.push('Adicione ao menos um motorista.');
    const final: string[] = [];
    if (!(Number(vcarga) > 0)) final.push('Informe o valor total da carga.');
    if (!(Number(qcarga) > 0)) final.push('Informe o peso total da carga.');
    return [geral, rota, veiculo, final];
  }, [serie, contratante, ufni, uffim, descargas, placa, tara, condutores, vcarga, qcarga]);
  const todasPendencias = pendencias.flat();
  const etapaAtual = pendencias.findIndex((p) => p.length > 0);

  // --- Listas ---------------------------------------------------------------
  const adicionarCarregamento = () => {
    if (municipioCargaNovo && !municipiosCarga.some((m) => m.codigo === municipioCargaNovo.codigo)) {
      setMunicipiosCarga([...municipiosCarga, municipioCargaNovo]);
    }
    setMunicipioCargaNovo(null);
  };
  const adicionarDescarregamento = () => {
    if (!municipioDescargaNovo) return;
    if (!descargas.some((d) => d.municipio?.codigo === municipioDescargaNovo.codigo)) {
      const nova = { id: uuidv4(), municipio: municipioDescargaNovo, chavesNfe: '', chavesCte: '' };
      setDescargas([...descargas, nova]);
      setDescargaEmEdicao(nova);
      if (!uffim) setUffim(municipioDescargaNovo.uf);
    }
    setMunicipioDescargaNovo(null);
  };
  const adicionarCondutor = () => {
    if (!condutorNovo.xnome.trim() || somenteDigitos(condutorNovo.cpf).length !== 11) {
      setMensagem({ tipo: 'error', texto: 'Informe o nome e o CPF (11 dígitos) do motorista.' });
      return;
    }
    setCondutores([...condutores, { id: uuidv4(), xnome: condutorNovo.xnome.trim(), cpf: somenteDigitos(condutorNovo.cpf) }]);
    setCondutorNovo({ xnome: '', cpf: '' });
  };
  const alterarSeguro = (id: string, campos: Partial<ISeguroForm>) => setSeguros((lista) => lista.map((s) => (s.id === id ? { ...s, ...campos } : s)));
  const adicionarSeguro = () => setSeguros([...seguros, { id: uuidv4(), responsavel: '1', cnpjResponsavel: '', seguradora: '', cnpjSeguradora: '', apolice: '', averbacoes: '' }]);

  // NF-e escolhidas em "Buscar NF-e" (DialogBuscarNfe): cada chave vai pra
  // cidade do destinatário (cria a linha se precisar), soma valor e peso,
  // inclui a cidade do emitente no carregamento e preenche a UF de
  // descarregamento se estiver vazia. Chave que já está no MDF-e é ignorada.
  const chavesJaIncluidas = useMemo(() => descargas.flatMap((d) => extrairChaves(d.chavesNfe)), [descargas]);
  const incluirNotasDoSistema = (notas: INfeParaMdfe[], erros: string[]) => {
    setOpenBuscarNfe(false);
    let lista = [...descargas];
    let cargas = [...municipiosCarga];
    let somaValor = 0;
    let somaPeso = 0;
    let incluidas = 0;
    const novasInfos: Record<string, { valor: number; pesoBruto: number }> = {};
    for (const nota of notas) {
      if (lista.some((d) => extrairChaves(d.chavesNfe).includes(nota.chave))) continue;
      let alvo = nota.destino ? lista.find((d) => d.municipio?.codigo === nota.destino!.codigo) : undefined;
      if (!alvo) {
        alvo = { id: uuidv4(), municipio: nota.destino, chavesNfe: '', chavesCte: '' };
        lista = [...lista, alvo];
      }
      const atualizado = { ...alvo, chavesNfe: [alvo.chavesNfe.trim(), nota.chave].filter(Boolean).join('\n') };
      lista = lista.map((d) => (d.id === atualizado.id ? atualizado : d));
      if (nota.origem && !cargas.some((m) => m.codigo === nota.origem!.codigo)) cargas = [...cargas, nota.origem];
      novasInfos[nota.chave] = { valor: nota.valor, pesoBruto: nota.pesoBruto };
      somaValor += nota.valor;
      somaPeso += nota.pesoBruto;
      incluidas++;
    }
    setDescargas(lista);
    setMunicipiosCarga(cargas);
    setInfoNotas((atual) => ({ ...atual, ...novasInfos }));
    if (somaValor) setVcarga((v) => (Number(v || 0) + somaValor).toFixed(2));
    if (somaPeso) setQcarga((q) => (Number(q || 0) + (cunid === '02' ? somaPeso / 1000 : somaPeso)).toFixed(cunid === '02' ? 4 : 3));
    if (!uffim) {
      const ufDestino = notas.find((n) => n.destino?.uf)?.destino?.uf;
      if (ufDestino) setUffim(ufDestino);
    }
    const partes = [`${incluidas} nota(s) incluída(s).`];
    if (notas.length > incluidas) partes.push(`${notas.length - incluidas} já estava(m) no MDF-e.`);
    if (erros.length) partes.push(`Não foi possível ler: ${erros.join('; ')}`);
    setMensagem({ tipo: erros.length ? 'error' : 'success', texto: partes.join(' ') });
  };

  // Todas as NF-e do MDF-e, com a cidade de descarregamento de cada uma.
  const notasVinculadas = useMemo(() => descargas.flatMap((d) => extrairChaves(d.chavesNfe).map((chave) => ({ chave, descarga: d }))), [descargas]);

  // Chave digitada à mão: vai pra cidade de descarregamento escolhida (ou a
  // única que existir). Valor e peso não vêm da chave — informar em Totais.
  const incluirChaveManual = () => {
    const chave = somenteDigitos(chaveManual);
    if (chave.length !== 44) { setMensagem({ tipo: 'error', texto: `A chave precisa ter 44 dígitos (tem ${chave.length}).` }); return; }
    if (chave.slice(20, 22) !== '55') { setMensagem({ tipo: 'error', texto: 'Essa chave não é de NF-e (modelo 55).' }); return; }
    if (chavesJaIncluidas.includes(chave)) { setMensagem({ tipo: 'error', texto: 'Essa nota já está vinculada ao MDF-e.' }); return; }
    const alvo = descargas.find((d) => d.id === descargaManualId) || (descargas.length === 1 ? descargas[0] : undefined);
    if (!alvo) {
      setMensagem({ tipo: 'error', texto: descargas.length ? 'Escolha a cidade de descarregamento da nota.' : 'Adicione primeiro a cidade de descarregamento da nota.' });
      return;
    }
    setDescargas((lista) => lista.map((d) => (d.id === alvo.id ? { ...d, chavesNfe: [d.chavesNfe.trim(), chave].filter(Boolean).join('\n') } : d)));
    setChaveManual('');
  };

  const removerNota = (chave: string, descargaId: string) => {
    setDescargas((lista) => lista.map((d) => (d.id === descargaId ? { ...d, chavesNfe: extrairChaves(d.chavesNfe).filter((c) => c !== chave).join('\n') } : d)));
    const info = infoNotas[chave];
    if (info) {
      if (info.valor) setVcarga((v) => Math.max(0, Number(v || 0) - info.valor).toFixed(2));
      if (info.pesoBruto) setQcarga((q) => Math.max(0, Number(q || 0) - (cunid === '02' ? info.pesoBruto / 1000 : info.pesoBruto)).toFixed(cunid === '02' ? 4 : 3));
      setInfoNotas((atual) => {
        const resto = { ...atual };
        delete resto[chave];
        return resto;
      });
    }
  };

  // --- Salvar (POST /mdfe ou PUT /mdfe/:id) --------------------------------------------------
  const salvar = async () => {
    if (todasPendencias.length) { setMensagem({ tipo: 'error', texto: todasPendencias[0] }); return; }
    setSalvando(true);
    const idmdfe = editando ? id : uuidv4();
    const docs = descargas.map((d) => {
      const nfes = extrairChaves(d.chavesNfe).map((chnfe) => ({ chnfe }));
      const ctes = extrairChaves(d.chavesCte).map((chcte) => ({ chcte }));
      return {
        iddocs: d.id, idmdfe,
        cmundescarga: d.municipio!.codigo, xmundescarga: d.municipio!.nome,
        ...(nfes.length ? { inffe: paraListaPg(nfes) } : {}),
        ...(ctes.length ? { infcte: paraListaPg(ctes) } : {}),
      };
    });
    const segurosCorpo = seguros.map((s) => ({
      idseguro: s.id, idmdfe,
      infresp: paraListaPg([{ respSeg: s.responsavel, ...(somenteDigitos(s.cnpjResponsavel) ? { cnpj: somenteDigitos(s.cnpjResponsavel) } : {}) }]),
      ...(s.seguradora.trim() ? { infseg: paraListaPg([{ xSeg: s.seguradora.trim(), cnpj: somenteDigitos(s.cnpjSeguradora), nApol: s.apolice.trim() }]) } : {}),
      ...(extrairChaves(s.averbacoes).length ? { naver: paraListaPg(extrairChaves(s.averbacoes)) } : {}),
    }));
    const idveiculo = idveiculoSalvo || uuidv4();
    const veiculos = [{
      idveiculos: idveiculo, idmdfe, cint: 1,
      placa: placa.trim().toUpperCase().replace(/[^A-Z0-9]/g, ''),
      renavam: somenteDigitos(renavam), tara: somenteDigitos(tara),
      capkg: somenteDigitos(capkg), capm3: somenteDigitos(capm3),
      condutor: paraListaPg(condutores.map((c) => ({ xnome: c.xnome, cpf: c.cpf }))),
      tprod, tcar, uf: ufVeiculo,
    }];
    const mdfe: INovoMdfe = {
      idmdfe, cuf: CODIGO_UF[ufni] || 0, tpamb, tptrasnp, serie: Number(serie),
      // Código numérico aleatório de 8 dígitos que compõe a chave de acesso.
      cmdf: cmdfSalvo || Math.floor(10000000 + Math.random() * 89999999),
      dhemi, dhiniviagem, idparceiro: contratante!.idparceiro, modal: '1', tpemis,
      ...(municipiosCarga.length ? { infmuncarrega: paraListaPg(municipiosCarga.map((m) => ({ cMunCarrega: m.codigo, xMunCarrega: m.nome }))) } : {}),
      ...(ufsPercurso.length ? { infpercurso: paraListaPg(ufsPercurso.map((UFPer) => ({ UFPer }))) } : {}),
      idveiculotracado: idveiculo, iddocs: docs[0]?.iddocs ?? null, idseguro: segurosCorpo[0]?.idseguro ?? null, idinfantt: null,
      qnfe: totais.qnfe, qcte: totais.qcte, qmdf: 0,
      vcarga: Number(vcarga), cunid, qcarga: Number(qcarga), ufni, uffim,
    };
    const resultado = editando
      ? await MdfeService.updateById(id, mdfe, docs, segurosCorpo, veiculos)
      : await MdfeService.create(mdfe, docs, segurosCorpo, veiculos);
    setSalvando(false);
    if (resultado instanceof Error) { setMensagem({ tipo: 'error', texto: resultado.message }); return; }
    setMensagem({ tipo: 'success', texto: 'MDF-e salvo com sucesso!' });
    setTimeout(() => navigate('/mdfe'), 900);
  };

  const seletor = (rotulo: string, valor: string | number, aoMudar: (v: string) => void, opcoes: { valor: string | number; rotulo: string }[]) => (
    <TextField select fullWidth size="small" label={rotulo} value={valor} onChange={(e) => aoMudar(e.target.value)}>
      {opcoes.map((o) => <MenuItem key={o.valor} value={o.valor}>{o.rotulo}</MenuItem>)}
    </TextField>
  );
  const opcoesUf = UFS.map((uf) => ({ valor: uf, rotulo: uf }));
  const cabecalhoTabela = { '& th': { fontWeight: 700, fontSize: '0.75rem', bgcolor: alpha(theme.palette.primary.main, 0.06) } };

  return (
    <LayoutBaseDePagina>
      <Snackbar open={!!mensagem} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={mensagem?.tipo === 'error' ? null : 1500} onClose={() => setMensagem(null)}>
        <Alert severity={mensagem?.tipo || 'info'} variant="filled" onClose={() => setMensagem(null)} sx={{ width: '100%' }}>{mensagem?.texto}</Alert>
      </Snackbar>

      <DialogBuscarNfe open={openBuscarNfe} chavesJaIncluidas={chavesJaIncluidas} onClose={() => setOpenBuscarNfe(false)} onConfirmar={incluirNotasDoSistema} />

      {/* Chaves de NF-e/CT-e de uma cidade de descarregamento */}
      <Dialog open={!!descargaEmEdicao} onClose={() => setDescargaEmEdicao(null)} fullWidth maxWidth="sm">
        <DialogTitle>Documentos — {descargaEmEdicao?.municipio?.nome} ({descargaEmEdicao?.municipio?.uf})</DialogTitle>
        <DialogContent dividers>
          {descargaEmEdicao && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={3} size="small" label="Chaves de NF-e (uma por linha)" value={descargaEmEdicao.chavesNfe}
                  helperText={`${extrairChaves(descargaEmEdicao.chavesNfe).length} chave(s)`}
                  onChange={(e) => setDescargaEmEdicao({ ...descargaEmEdicao, chavesNfe: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} size="small" label="Chaves de CT-e (uma por linha)" value={descargaEmEdicao.chavesCte}
                  helperText={`${extrairChaves(descargaEmEdicao.chavesCte).length} chave(s)`}
                  onChange={(e) => setDescargaEmEdicao({ ...descargaEmEdicao, chavesCte: e.target.value })} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescargaEmEdicao(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => {
            if (descargaEmEdicao) setDescargas((lista) => lista.map((d) => (d.id === descargaEmEdicao.id ? descargaEmEdicao : d)));
            setDescargaEmEdicao(null);
          }}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Barra de ações (fixa no topo) */}
      <Paper
        elevation={6}
        sx={{
          position: 'sticky', top: 8, mx: { xs: 1, sm: 2 }, mt: 1, px: 2, py: 1.25, borderRadius: 2.5,
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#0d2a4f',
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', zIndex: 5,
        }}
      >
        {[
          { icone: 'search', rotulo: 'Buscar NF-e', acao: () => setOpenBuscarNfe(true) },
          { icone: 'verified_user', rotulo: 'Seguro', acao: adicionarSeguro },
          { icone: 'arrow_back', rotulo: 'Voltar', acao: () => navigate('/mdfe') },
        ].map((b) => (
          <Button key={b.rotulo} size="small" variant="outlined" startIcon={<Icon>{b.icone}</Icon>} onClick={b.acao}
            sx={{ color: '#fff', borderColor: alpha('#fff', 0.35), '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) } }}>
            {b.rotulo}
          </Button>
        ))}
        <Box flex={1} />
        <Button variant="contained" color="success" disableElevation disabled={salvando || carregando || bloqueado} onClick={salvar}
          startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <Icon>save</Icon>}>
          {salvando ? 'Salvando...' : 'Salvar MDF-e'}
        </Button>
      </Paper>

      <Box mx={{ xs: 1, sm: 2 }} my={1.5}>
        {/* Cabeçalho: título, situação, validação e etapas */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5, boxShadow: theme.shadows[1] }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5} display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon sx={{ fontSize: 30 }}>local_shipping</Icon>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Emissão de MDF-e</Typography>
                <Typography variant="caption" color="text.secondary">Manifesto Eletrônico de Documentos Fiscais</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} display="flex" gap={2} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Status do MDF-e</Typography>
                {carregando ? <CircularProgress size={18} /> : editando
                  ? <Chip size="small" color={situacao.cor} label={situacao.rotulo} />
                  : <Chip size="small" color="primary" icon={<Icon sx={{ fontSize: 16 }}>edit_note</Icon>} label="Em digitação" />}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Validação</Typography>
                <Tooltip title={todasPendencias.join('\n')} arrow>
                  <Chip size="small" variant="outlined" color={todasPendencias.length ? 'warning' : 'success'}
                    icon={<Icon sx={{ fontSize: 16 }}>{todasPendencias.length ? 'error_outline' : 'check_circle'}</Icon>}
                    label={todasPendencias.length ? `${todasPendencias.length} pendência(s)` : 'Dados válidos'} />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stepper activeStep={etapaAtual === -1 ? ETAPAS.length : etapaAtual} alternativeLabel={!smDown} orientation={smDown ? 'vertical' : 'horizontal'}>
                {ETAPAS.map((etapa, i) => (
                  <Step key={etapa} completed={pendencias[i].length === 0}>
                    <StepLabel><Typography variant="caption">{etapa}</Typography></StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
          </Grid>
          {tpamb !== 1 && <Alert severity="warning" sx={{ mt: 1.5 }}>A empresa está em ambiente de <b>Homologação</b> — o MDF-e não tem valor fiscal.</Alert>}
        </Paper>

        <Grid container spacing={2}>
          {/* Dados gerais */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, boxShadow: theme.shadows[1] }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="Número" value={numeroSalvo ?? ''} placeholder="Gerado ao salvar" InputLabelProps={{ shrink: true }} disabled helperText={editando ? ' ' : 'O servidor numera ao salvar'} /></Grid>
                <Grid item xs={6} sm={2} md={1}><TextField fullWidth size="small" label="Série" value={serie} onChange={(e) => setSerie(somenteDigitos(e.target.value))} /></Grid>
                <Grid item xs={12} sm={7} md={3}><TextField fullWidth size="small" label="Emitente" value={emitente} disabled /></Grid>
                <Grid item xs={6} sm={4} md={2}><TextField fullWidth size="small" type="date" label="Data de Emissão" InputLabelProps={{ shrink: true }} value={dhemi} onChange={(e) => setDhemi(e.target.value)} /></Grid>
                <Grid item xs={6} sm={4} md={2}>{seletor('UF Carregamento', ufni, setUfni, opcoesUf)}</Grid>
                <Grid item xs={6} sm={4} md={2}>{seletor('UF Descarregamento', uffim, setUffim, opcoesUf)}</Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Viagem */}
          <Grid item xs={12}>
            <Cartao icone="directions_bus" titulo="Viagem">
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2}><TextField fullWidth size="small" type="date" label="Data Início Viagem" InputLabelProps={{ shrink: true }} value={dhiniviagem} onChange={(e) => setDhiniviagem(e.target.value)} /></Grid>
                <Grid item xs={12} sm={8} md={4}>
                  <Autocomplete
                    size="small" options={opcoesContratante} value={contratante} filterOptions={(x) => x}
                    getOptionLabel={(p) => p.nome}
                    isOptionEqualToValue={(a, b) => a.idparceiro === b.idparceiro}
                    onInputChange={(_, texto, motivo) => { if (motivo === 'input') setBuscaContratante(texto); }}
                    onChange={(_, novo) => setContratante(novo)}
                    renderInput={(params) => <TextField {...params} label="Contratante" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>{seletor('Tipo de Transportador', tptrasnp, (v) => setTptrasnp(Number(v)), TIPOS_TRANSPORTADOR)}</Grid>
                <Grid item xs={6} sm={3} md={2}>{seletor('Tipo de Emissão', tpemis, (v) => setTpemis(Number(v)), [{ valor: 1, rotulo: '1 - Normal' }, { valor: 2, rotulo: '2 - Contingência' }])}</Grid>
                <Grid item xs={6} sm={3} md={1}><TextField fullWidth size="small" label="Modal" value="Rodoviário" disabled /></Grid>
              </Grid>
            </Cartao>
          </Grid>

          {/* Cidades de carregamento */}
          <Grid item xs={12} md={6}>
            <Cartao icone="place" titulo="Cidades de Carregamento" acao={<BotaoAdicionar onClick={adicionarCarregamento} disabled={!municipioCargaNovo} />}>
              <Box mb={1.5}><CampoMunicipio rotulo="Cidade de carregamento" valor={municipioCargaNovo} aoMudar={setMunicipioCargaNovo} /></Box>
              <Table size="small" sx={cabecalhoTabela}>
                <TableHead><TableRow><TableCell>IBGE</TableCell><TableCell>Cidade</TableCell><TableCell width={48} /></TableRow></TableHead>
                <TableBody>
                  {!municipiosCarga.length && <LinhaVazia colunas={3} texto="Nenhuma cidade de carregamento" />}
                  {municipiosCarga.map((m) => (
                    <TableRow key={m.codigo}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{m.codigo}</TableCell>
                      <TableCell>{m.nome} ({m.uf})</TableCell>
                      <TableCell><IconButton size="small" color="error" onClick={() => setMunicipiosCarga(municipiosCarga.filter((x) => x.codigo !== m.codigo))}><Icon fontSize="small">delete</Icon></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Cartao>
          </Grid>

          {/* Cidades de descarregamento (com os documentos de cada uma) */}
          <Grid item xs={12} md={6}>
            <Cartao icone="place" titulo="Cidades de Descarregamento" acao={<BotaoAdicionar onClick={adicionarDescarregamento} disabled={!municipioDescargaNovo} />}>
              <Box mb={1.5}><CampoMunicipio rotulo="Cidade de descarregamento" valor={municipioDescargaNovo} aoMudar={setMunicipioDescargaNovo} /></Box>
              <Table size="small" sx={cabecalhoTabela}>
                <TableHead><TableRow><TableCell>IBGE</TableCell><TableCell>Cidade</TableCell><TableCell align="center">Documentos</TableCell><TableCell width={84} /></TableRow></TableHead>
                <TableBody>
                  {!descargas.length && <LinhaVazia colunas={4} texto="Nenhuma cidade de descarregamento" />}
                  {descargas.map((d) => {
                    const qn = extrairChaves(d.chavesNfe).length;
                    const qc = extrairChaves(d.chavesCte).length;
                    return (
                      <TableRow key={d.id}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{d.municipio?.codigo || '-'}</TableCell>
                        <TableCell>{d.municipio ? `${d.municipio.nome} (${d.municipio.uf})` : '-'}</TableCell>
                        <TableCell align="center">
                          {qn > 0 && <Chip size="small" variant="outlined" label={`${qn} NF-e`} sx={{ height: 22, mr: 0.5 }} />}
                          {qc > 0 && <Chip size="small" variant="outlined" label={`${qc} CT-e`} sx={{ height: 22 }} />}
                          {!qn && !qc && <Chip size="small" color="warning" variant="outlined" label="Sem documentos" sx={{ height: 22 }} />}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Chaves de NF-e/CT-e"><IconButton size="small" onClick={() => setDescargaEmEdicao(d)}><Icon fontSize="small">description</Icon></IconButton></Tooltip>
                          <IconButton size="small" color="error" onClick={() => setDescargas(descargas.filter((x) => x.id !== d.id))}><Icon fontSize="small">delete</Icon></IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Cartao>
          </Grid>

          {/* Notas vinculadas */}
          <Grid item xs={12}>
            <Cartao icone="receipt_long" titulo={`Notas Vinculadas (${notasVinculadas.length})`}
              acao={<BotaoAdicionar onClick={() => setOpenBuscarNfe(true)}>Incluir Notas</BotaoAdicionar>}>
              <Grid container spacing={1.5} mb={1.5} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Chave de acesso da NF-e (digitar ou colar)" value={formatarChave(chaveManual)}
                    helperText={`${chaveManual.length}/44 dígitos`}
                    onChange={(e) => setChaveManual(somenteDigitos(e.target.value).slice(0, 44))}
                    onKeyDown={(e) => { if (e.key === 'Enter') incluirChaveManual(); }}
                    inputProps={{ style: { fontFamily: 'monospace' } }} />
                </Grid>
                <Grid item xs={8} md={4}>
                  <TextField select fullWidth size="small" label="Cidade de descarregamento"
                    helperText={descargas.length ? ' ' : 'Adicione uma cidade de descarregamento'}
                    value={descargas.some((d) => d.id === descargaManualId) ? descargaManualId : (descargas.length === 1 ? descargas[0].id : '')}
                    onChange={(e) => setDescargaManualId(e.target.value)} disabled={!descargas.length}>
                    {descargas.map((d) => <MenuItem key={d.id} value={d.id}>{d.municipio ? `${d.municipio.nome} (${d.municipio.uf})` : '-'}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4} md={2}>
                  <Button fullWidth variant="outlined" startIcon={<Icon>keyboard</Icon>} onClick={incluirChaveManual} disabled={chaveManual.length !== 44} sx={{ height: 40 }}>Incluir</Button>
                </Grid>
              </Grid>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={cabecalhoTabela}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Chave de acesso</TableCell><TableCell align="right">Nº</TableCell><TableCell align="right">Série</TableCell>
                      <TableCell>Descarregamento</TableCell><TableCell align="right">Valor</TableCell><TableCell align="right">Peso (kg)</TableCell>
                      <TableCell align="center">Origem</TableCell><TableCell width={48} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!notasVinculadas.length && <LinhaVazia colunas={8} texto="Nenhuma nota vinculada — use Incluir Notas ou digite a chave" />}
                    {notasVinculadas.map(({ chave, descarga }) => {
                      const info = infoNotas[chave];
                      return (
                        <TableRow key={chave}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatarChave(chave)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{chave.length === 44 ? numeroDaChave(chave) : '-'}</TableCell>
                          <TableCell align="right">{chave.length === 44 ? serieDaChave(chave) : '-'}</TableCell>
                          <TableCell>{descarga.municipio ? `${descarga.municipio.nome} (${descarga.municipio.uf})` : '-'}</TableCell>
                          <TableCell align="right">{info ? info.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '-'}</TableCell>
                          <TableCell align="right">{info ? info.pesoBruto.toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : '-'}</TableCell>
                          <TableCell align="center">
                            <Chip size="small" variant="outlined" color={info ? 'primary' : 'default'} label={info ? 'Sistema' : 'Manual'} sx={{ height: 22 }} />
                          </TableCell>
                          <TableCell><IconButton size="small" color="error" onClick={() => removerNota(chave, descarga.id)}><Icon fontSize="small">delete</Icon></IconButton></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Cartao>
          </Grid>

          {/* Rota */}
          <Grid item xs={12} md={6}>
            <Cartao icone="route" titulo="Rota">
              {/* Escolha simples (fecha a cada clique): a UF entra no fim do
                  percurso, na ordem em que o caminhão passa. */}
              <TextField select fullWidth size="small" label="Adicionar UF de percurso (entre o carregamento e o descarregamento)" value=""
                onChange={(e) => { if (e.target.value && !ufsPercurso.includes(e.target.value)) setUfsPercurso([...ufsPercurso, e.target.value]); }}>
                {UFS.filter((uf) => !ufsPercurso.includes(uf)).map((uf) => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
              </TextField>
              <Box mt={1.5} display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                <Chip size="small" color="primary" label={ufni || '?'} />
                {ufsPercurso.map((uf) => (
                  <Box key={uf} display="flex" alignItems="center" gap={0.75}>
                    <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>arrow_forward</Icon>
                    <Chip size="small" label={uf} onDelete={() => setUfsPercurso(ufsPercurso.filter((x) => x !== uf))} />
                  </Box>
                ))}
                <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>arrow_forward</Icon>
                <Chip size="small" color="primary" label={uffim || '?'} />
              </Box>
            </Cartao>
          </Grid>

          {/* Veículo de tração */}
          <Grid item xs={12} md={6}>
            <Cartao icone="local_shipping" titulo="Veículo de Tração">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small" options={opcoesTransportadora} value={transportadora} filterOptions={(x) => x}
                    getOptionLabel={(o) => o.fantasia || o.nome}
                    isOptionEqualToValue={(a, b) => a.idtransportadora === b.idtransportadora}
                    onInputChange={(_, texto, motivo) => { if (motivo === 'input') setBuscaTransportadora(texto); }}
                    onChange={(_, nova) => setTransportadora(nova)}
                    noOptionsText="Nenhuma transportadora"
                    renderInput={(params) => <TextField {...params} label="Transportadora (cadastro)" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small" options={veiculosDaTransportadora} value={veiculoCadastrado} disabled={!transportadora}
                    getOptionLabel={(v) => `${v.placa}${v.uf ? ` (${v.uf})` : ''}`}
                    isOptionEqualToValue={(a, b) => a.idveiculo === b.idveiculo}
                    onChange={(_, novo) => preencherVeiculo(novo)}
                    noOptionsText="Nenhum veículo"
                    renderInput={(params) => (
                      <TextField {...params} label="Veículo da transportadora"
                        helperText={transportadora ? 'Preenche os dados abaixo' : 'Escolha a transportadora'}
                        InputProps={{ ...params.InputProps, endAdornment: <>{carregandoVeiculo && <CircularProgress size={16} />}{params.InputProps.endAdornment}</> }} />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Placa" value={placa} onChange={(e) => setPlaca(e.target.value.toUpperCase())} inputProps={{ maxLength: 8 }} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="RENAVAM" value={renavam} onChange={(e) => setRenavam(somenteDigitos(e.target.value))} /></Grid>
                <Grid item xs={6} sm={4}>{seletor('UF', ufVeiculo, setUfVeiculo, opcoesUf)}</Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Tara (kg)" value={tara} onChange={(e) => setTara(somenteDigitos(e.target.value))} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Capacidade (kg)" value={capkg} onChange={(e) => setCapkg(somenteDigitos(e.target.value))} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Capacidade (m³)" value={capm3} onChange={(e) => setCapm3(somenteDigitos(e.target.value))} /></Grid>
                <Grid item xs={12} sm={6}>{seletor('Tipo de Rodado', tprod, setTprod, TIPOS_RODADO)}</Grid>
                <Grid item xs={12} sm={6}>{seletor('Tipo de Carroceria', tcar, setTcar, TIPOS_CARROCERIA)}</Grid>
              </Grid>
            </Cartao>
          </Grid>

          {/* Motoristas */}
          <Grid item xs={12} md={6}>
            <Cartao icone="person" titulo="Motoristas" acao={<BotaoAdicionar onClick={adicionarCondutor} />}>
              <Grid container spacing={1.5} mb={1.5}>
                <Grid item xs={12} sm={7}><TextField fullWidth size="small" label="Nome do motorista" value={condutorNovo.xnome} onChange={(e) => setCondutorNovo({ ...condutorNovo, xnome: e.target.value })} /></Grid>
                <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="CPF" value={condutorNovo.cpf} onChange={(e) => setCondutorNovo({ ...condutorNovo, cpf: somenteDigitos(e.target.value).slice(0, 11) })}
                  onKeyDown={(e) => { if (e.key === 'Enter') adicionarCondutor(); }} /></Grid>
              </Grid>
              <Table size="small" sx={cabecalhoTabela}>
                <TableHead><TableRow><TableCell>Motorista</TableCell><TableCell>CPF</TableCell><TableCell width={48} /></TableRow></TableHead>
                <TableBody>
                  {!condutores.length && <LinhaVazia colunas={3} texto="Nenhum motorista" />}
                  {condutores.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.xnome}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{formatarCpf(c.cpf)}</TableCell>
                      <TableCell><IconButton size="small" color="error" onClick={() => setCondutores(condutores.filter((x) => x.id !== c.id))}><Icon fontSize="small">delete</Icon></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Cartao>
          </Grid>

          {/* Totais */}
          <Grid item xs={12} md={6}>
            <Cartao icone="calculate" titulo="Totais">
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Qtd. NF-e" value={totais.qnfe} disabled /></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Qtd. CT-e" value={totais.qcte} disabled /></Grid>
                <Grid item xs={12} sm={6}>{seletor('Unidade de Medida', cunid, setCunid, UNIDADES_CARGA)}</Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label={`Peso Bruto (${cunid === '01' ? 'KG' : 'TON'})`} value={qcarga} onChange={(e) => setQcarga(e.target.value.replace(',', '.').replace(/[^\d.]/g, ''))} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Valor da Carga (R$)" value={vcarga} onChange={(e) => setVcarga(e.target.value.replace(',', '.').replace(/[^\d.]/g, ''))} /></Grid>
              </Grid>
            </Cartao>
          </Grid>

          {/* Seguro */}
          <Grid item xs={12}>
            <Cartao icone="verified_user" titulo="Seguro da Carga" acao={<BotaoAdicionar onClick={adicionarSeguro} />}>
              {!seguros.length && <Typography variant="body2" color="text.secondary">Nenhum seguro informado (opcional).</Typography>}
              {seguros.map((s) => (
                <Grid container spacing={1.5} key={s.id} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} sm={4} md={2}>{seletor('Responsável', s.responsavel, (v) => alterarSeguro(s.id, { responsavel: v as '1' | '2' }), [{ valor: '1', rotulo: '1 - Emitente' }, { valor: '2', rotulo: '2 - Contratante' }])}</Grid>
                  <Grid item xs={12} sm={4} md={2}><TextField fullWidth size="small" label="CNPJ/CPF responsável" value={s.cnpjResponsavel} onChange={(e) => alterarSeguro(s.id, { cnpjResponsavel: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={4} md={2}><TextField fullWidth size="small" label="Seguradora" value={s.seguradora} onChange={(e) => alterarSeguro(s.id, { seguradora: e.target.value })} /></Grid>
                  <Grid item xs={6} sm={4} md={2}><TextField fullWidth size="small" label="CNPJ seguradora" value={s.cnpjSeguradora} onChange={(e) => alterarSeguro(s.id, { cnpjSeguradora: e.target.value })} /></Grid>
                  <Grid item xs={6} sm={4} md={1.5}><TextField fullWidth size="small" label="Nº apólice" value={s.apolice} onChange={(e) => alterarSeguro(s.id, { apolice: e.target.value })} /></Grid>
                  <Grid item xs={10} sm={3} md={2}><TextField fullWidth size="small" label="Averbações" value={s.averbacoes} onChange={(e) => alterarSeguro(s.id, { averbacoes: e.target.value })} /></Grid>
                  <Grid item xs={2} sm={1} md={0.5}><IconButton color="error" onClick={() => setSeguros(seguros.filter((x) => x.id !== s.id))}><Icon>delete</Icon></IconButton></Grid>
                </Grid>
              ))}
            </Cartao>
          </Grid>
        </Grid>
      </Box>
    </LayoutBaseDePagina>
  );
};
